import {
  LayoutDashboard,
  Gamepad2,
  Settings,
  MonitorPlay,
  Clapperboard,
  Users,
  Trophy,
  Swords,
  DollarSign,
  QrCode,
  Building2,
  Target,
  MessageSquare,
  ShoppingBag,
  CreditCard,
  Plug,
  type LucideIcon,
} from "lucide-react";

// Configuración de permisos por rol para las páginas del menú
export type Role = "owner" | "admin" | "member" | "coach" | "player";

export interface PagePermission {
  path: string;
  name: string;
  roles: Role[];
  icon: LucideIcon;
}

export interface NavigationPermission {
  path?: string;
  name: string;
  roles: Role[];
  subItems?: PagePermission[];
  icon: LucideIcon;
}

// Configuración de permisos para cada página del menú
export const navigationPermissions: NavigationPermission[] = [
  {
    path: "/dashboard",
    name: "Dashboard",
    roles: ["owner", "admin", "member", "coach", "player"],
    icon: LayoutDashboard,
  },
  {
    path: "/overlays",
    name: "Overlays",
    roles: ["owner", "admin", "member"],
    icon: Gamepad2,
  },
  {
    path: "/scrims",
    name: "Scrims",
    roles: ["owner", "admin", "member", "coach", "player"],
    icon: Swords,
  },
  {
    path: "/vods",
    name: "VOD Review",
    roles: ["owner", "admin", "member", "coach", "player"],
    icon: Clapperboard,
  },
  {
    path: "/quotes",
    name: "Cotizaciones",
    roles: ["owner", "admin", "member"],
    icon: DollarSign,
  },
  {
    path: "/qr",
    name: "Generador QR",
    roles: ["owner", "admin", "member"],
    icon: QrCode,
  },
  {
    path: "/tournaments/admin",
    name: "Torneos",
    roles: ["owner", "admin", "member"],
    icon: Trophy,
  },
  {
    path: "/merch/admin",
    name: "Merch",
    roles: ["owner", "admin"],
    icon: ShoppingBag,
  },
  {
    path: "/subscriptions/admin",
    name: "Suscripciones",
    roles: ["owner", "admin"],
    icon: CreditCard,
  },
  {
    path: "/integrations",
    name: "Integraciones",
    roles: ["owner", "admin"],
    icon: Plug,
    subItems: [
      {
        path: "/integrations/discord",
        name: "Discord",
        roles: ["owner", "admin", "member"],
        icon: MessageSquare,
      },
    ],
  },
  {
    path: "/control",
    name: "Mantenedores",
    roles: ["owner", "admin"],
    icon: Settings,
    subItems: [
      {
        path: "/control/creators",
        name: "Creadores",
        roles: ["owner", "admin", "member"],
        icon: Users,
      },
      {
        path: "/control/clients",
        name: "Clientes",
        roles: ["owner", "admin", "member"],
        icon: MonitorPlay,
      },
      {
        path: "/control/rosters",
        name: "Rosters",
        roles: ["owner", "admin", "member", "coach"],
        icon: Trophy,
      },
      {
        path: "/control/organization",
        name: "Organización",
        roles: ["owner", "admin"],
        icon: Building2,
      },
    ],
  },
  {
    path: "/activities",
    name: "Actividades",
    roles: ["owner", "admin", "member", "coach"],
    icon: Target,
  },
];

// Función para verificar si un rol tiene acceso a una página
export const hasPageAccess = (path: string, role: Role): boolean => {
  for (const item of navigationPermissions) {
    const roleSet = new Set(item.roles);
    if (item.path === path) {
      return roleSet.has(role);
    }

    if (item.subItems) {
      for (const subItem of item.subItems) {
        if (subItem.path === path) {
          return new Set(subItem.roles).has(role);
        }
      }
    }
  }
  return false;
};

// Función para obtener todas las páginas accesibles para un rol
export const getAccessiblePages = (role: Role): string[] => {
  const accessiblePages: string[] = [];

  for (const item of navigationPermissions) {
    if (new Set(item.roles).has(role)) {
      if (item.path) {
        accessiblePages.push(item.path);
      }

      if (item.subItems) {
        for (const subItem of item.subItems) {
          if (new Set(subItem.roles).has(role)) {
            accessiblePages.push(subItem.path);
          }
        }
      }
    }
  }

  return accessiblePages;
};

// Función para obtener la configuración de permisos filtrada por rol
export const getNavigationForRole = (role: Role): NavigationPermission[] => {
  return navigationPermissions
    .map((item) => {
      if (item.subItems) {
        return {
          ...item,
          subItems: item.subItems.filter((subItem) =>
            subItem.roles.includes(role),
          ),
        };
      }
      return item;
    })
    .filter((item) => {
      if (item.subItems) {
        return item.subItems.length > 0;
      }
      return item.roles.includes(role);
    });
};

// Configuración de permisos detallados por acción
export const actionPermissions = {
  creators: {
    view: ["owner", "admin", "member"],
    create: ["owner", "admin", "member"],
    update: ["owner", "admin", "member"],
    delete: ["owner", "admin"],
  },
  clients: {
    view: ["owner", "admin", "member"],
    create: ["owner", "admin", "member"],
    update: ["owner", "admin", "member"],
    delete: ["owner", "admin"],
  },
  games: {
    view: ["owner", "admin", "member", "coach"],
    create: ["owner", "admin", "member"],
    update: ["owner", "admin", "member"],
    delete: ["owner", "admin"],
  },
  rosters: {
    view: ["owner", "admin", "member", "coach"],
    create: ["owner", "admin", "member", "coach"],
    update: ["owner", "admin", "member", "coach"],
    delete: ["owner", "admin"],
  },
  organization: {
    view: ["owner", "admin"],
    create: ["owner"],
    update: ["owner", "admin"],
    delete: ["owner"],
  },
  overlays: {
    view: ["owner", "admin", "member"],
    create: ["owner", "admin", "member"],
    update: ["owner", "admin", "member"],
    delete: ["owner", "admin"],
  },
  quotes: {
    view: ["owner", "admin", "member"],
    create: ["owner", "admin", "member"],
    update: ["owner", "admin", "member"],
    delete: ["owner", "admin"],
  },
  activities: {
    view: ["owner", "admin", "member", "coach"],
    create: ["owner", "admin", "member", "coach"],
    update: ["owner", "admin", "member", "coach"],
    delete: ["owner", "admin"],
  },
  tournaments: {
    view: ["owner", "admin", "member"],
    create: ["owner", "admin", "member"],
    update: ["owner", "admin", "member"],
    delete: ["owner", "admin"],
  },
  merch: {
    view: ["owner", "admin"],
    create: ["owner", "admin"],
    update: ["owner", "admin"],
    delete: ["owner", "admin"],
  },
  subscriptions: {
    view: ["owner", "admin"],
    create: ["owner", "admin"],
    update: ["owner", "admin"],
    delete: ["owner", "admin"],
  },
  scrims: {
    view: ["owner", "admin", "member", "coach", "player"],
    create: ["owner", "admin", "coach"],
    update: ["owner", "admin", "coach"],
    delete: ["owner", "admin"],
  },
  vods: {
    view: ["owner", "admin", "member", "coach", "player"],
    create: ["owner", "admin", "member", "coach"],
    update: ["owner", "admin", "member", "coach"],
    delete: ["owner", "admin"],
  },
};

// Función para verificar permisos de acción específicos
export const hasActionPermission = (
  resource: string,
  action: string,
  role: Role,
): boolean => {
  const resourcePermissions =
    actionPermissions[resource as keyof typeof actionPermissions];
  if (!resourcePermissions) return false;

  const actionRoles =
    resourcePermissions[action as keyof typeof resourcePermissions];
  if (!actionRoles) return false;

  return actionRoles.includes(role);
};
